import { Box } from '@mui/material';
import { AutoIsoPlayer } from '@/features/autoIso/types/AutoIsoModel';
import { TrackedItem } from './TrackedItem';
import { memo, useCallback, useMemo } from 'react';

export type PlayerItemProps = {
  player: AutoIsoPlayer;
  teamLogoPath: string;
  teamColor: string;
  onPlayerClick?: (player: AutoIsoPlayer) => void;
  isTrackedObject?: boolean;
  sx?: object;
  readOnly?: boolean;
  trackedItemSx?: object;
  disableNameEllipsis?: boolean;
};

/**
 * Memoized player item component that displays a single player's information.
 * Optimized to prevent unnecessary re-renders through memoization.
 */
export const PlayerItem = memo(
  ({
    player,
    teamLogoPath,
    teamColor,
    onPlayerClick,
    isTrackedObject,
    sx,
    readOnly,
    trackedItemSx,
    disableNameEllipsis,
  }: PlayerItemProps) => {
    const handleClick = useCallback(() => {
      onPlayerClick?.(player);
    }, [onPlayerClick, player]);

    // Memoize style object to prevent re-creation
    const boxStyle = useMemo(
      () => ({
        cursor: onPlayerClick ? 'pointer' : 'default',
        width: '100%',
        ...sx
      }),
      [onPlayerClick,sx],
    );

    return (
      <Box onClick={handleClick} style={boxStyle}>
        <TrackedItem
          id={player.id}
          displayName={player.displayName}
          playerNumber={player.playerNumber}
          playerPosition={player.playerPosition}
          playerHeadshot={player.headshot || ''}
          teamLogo={teamLogoPath}
          teamColor={teamColor}
          isTrackedObject={isTrackedObject}
          readOnly={readOnly}
          sx={trackedItemSx}
          disableNameEllipsis={disableNameEllipsis}
        />
      </Box>
    );
  },
);

PlayerItem.displayName = 'PlayerItem';
